import { axiosInstance } from '.'
import {
  DIVIDEND_DECLARATIONS_UPLOAD_V1,
  DIVIDEND_PAYOUTS_PREVIEW_V1,
  DIVIDEND_PAYOUTS_V1,
  INTEREST_PAYOUTS_PREVIEW_V1,
  INTEREST_PAYOUTS_V1,
} from './endpoints'

export interface InterestPayoutRequest {
  opportunity_ids: number[]
  /** When set, only these investments receive payout (must belong to selected opportunities). */
  investment_ids?: number[]
  /** Admin overrides for reinvestment disposition per investment (confirm modal). */
  reinvest_overrides?: Array<{ investment_id: number; reinvest: boolean }>
  payout_date?: string
  period_start_date: string
  period_end_date: string
}

export type InterestPayoutLineStatus =
  | 'processed'
  | 'reinvested'
  | 'skipped_duplicate'
  | 'skipped_zero_interest'
  | 'skipped_raise_goal'

export interface InterestPayoutLine {
  investor_id: number
  investment_id: number
  status: InterestPayoutLineStatus
  interest_rate?: number
  interest_amount?: number
  transaction_id?: number
  reinvestment_investment_id?: number
}

export interface InterestPayoutResponse {
  total_opportunities_processed: number
  total_transactions_created: number
  total_payout_amount: number
  total_reinvested_amount: number
  total_reinvestments_created: number
  results: Array<{
    opportunity_id: number
    opportunity_title: string
    transactions_created: number
    total_payout_amount: number
    total_reinvested_amount: number
    reinvestments_created: number
    investor_payouts: Array<{
      investor_id: number
      investment_amount: number
      interest_rate: number
      interest_amount: number
      transaction_id: number
      disposition?: 'payout' | 'reinvested'
      reinvestment_investment_id?: number
    }>
    payout_lines: InterestPayoutLine[]
  }>
}

export interface InterestPayoutPreviewResponse {
  total_investments: number
  total_investors: number
  total_estimated_payout_dollars: number
  total_estimated_reinvested_dollars: number
  would_create_transactions: number
  would_create_reinvestments: number
  skipped_duplicate: number
  skipped_zero_interest: number
  skipped_raise_goal?: number
  payout_date: string
  period_start_date: string
  period_end_date: string
  results: InterestPayoutResponse['results']
}

export type DividendPayoutLineStatus =
  | 'would_process'
  | 'processed'
  | 'skipped_duplicate'
  | 'skipped_zero'

export interface DividendPayoutLine {
  investor_id: number
  units_held: number
  dividend_per_unit_cents: number
  is_custom_rate: boolean
  dividend_amount: number
  status: DividendPayoutLineStatus
  transaction_id?: number
}

export interface DividendPayoutPreviewResponse {
  dividend_declaration_id: number
  opportunity_id: number
  opportunity_title: string
  dividend_per_unit: number
  ex_dividend_date: string
  dividend_payment_date: string
  total_estimated_payout_cents: number
  total_estimated_payout_dollars: number
  would_create_transactions: number
  skipped_duplicate: number
  skipped_zero: number
  payout_lines: DividendPayoutLine[]
}

export interface DividendPayoutResponse {
  dividend_declaration_id: number
  total_transactions_created: number
  total_payout_amount: number
  result: {
    opportunity_id: number
    opportunity_title: string
    transactions_created: number
    total_payout_amount: number
    dividend_per_unit: number
    ex_dividend_date: string
    investor_payouts: Array<{
      investor_id: number
      units_held: number
      dividend_amount: number
      transaction_id: number
    }>
    payout_lines: DividendPayoutLine[]
  }
}

export interface DividendDeclarationPayload {
  opportunity_id: number
  declaration_date: string
  ex_dividend_date: string
  dividend_payment_date: string
  /** Cents */
  dividend_per_unit: number
}

export interface DividendDeclarationUploadResponse {
  success: boolean
  rowsProcessed: number
  declarations: DividendDeclarationPayload[]
  previews: DividendPayoutPreviewResponse[]
}

export const transactionServices = {
  previewInterestPayouts: async (
    payload: InterestPayoutRequest,
  ): Promise<{
    data?: InterestPayoutPreviewResponse
    status?: number
    error?: string
  }> => {
    try {
      const response = await axiosInstance.post(INTEREST_PAYOUTS_PREVIEW_V1, payload)
      return { data: response.data, status: response.status }
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { error?: string } }
        message?: string
      }
      return {
        status: err.response?.status,
        error: err.response?.data?.error || err.message,
      }
    }
  },
  createInterestPayouts: async (
    payload: InterestPayoutRequest,
  ): Promise<{ data?: InterestPayoutResponse; status?: number; error?: string }> => {
    try {
      const response = await axiosInstance.post(INTEREST_PAYOUTS_V1, payload)
      return { data: response.data, status: response.status }
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { error?: string } }
        message?: string
      }
      return {
        status: err.response?.status,
        error: err.response?.data?.error || err.message,
      }
    }
  },
  uploadDividendDeclarationsCsv: async (
    formData: FormData,
  ): Promise<{
    data?: DividendDeclarationUploadResponse
    status?: number
    error?: string
  }> => {
    try {
      const response = await axiosInstance.post(DIVIDEND_DECLARATIONS_UPLOAD_V1, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return { data: response.data, status: response.status }
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { error?: string } }
        message?: string
      }
      return {
        status: err.response?.status,
        error: err.response?.data?.error || err.message,
      }
    }
  },
  previewDividendPayouts: async (payload: {
    dividend_declaration_id?: number
    declaration?: DividendDeclarationPayload
  }): Promise<{
    data?: DividendPayoutPreviewResponse
    status?: number
    error?: string
  }> => {
    try {
      const response = await axiosInstance.post(DIVIDEND_PAYOUTS_PREVIEW_V1, payload)
      return { data: response.data, status: response.status }
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { error?: string } }
        message?: string
      }
      return {
        status: err.response?.status,
        error: err.response?.data?.error || err.message,
      }
    }
  },
  createDividendPayouts: async (payload: {
    dividend_declaration_id?: number
    declaration?: DividendDeclarationPayload
  }): Promise<{ data?: DividendPayoutResponse; status?: number; error?: string }> => {
    try {
      const response = await axiosInstance.post(DIVIDEND_PAYOUTS_V1, payload)
      return { data: response.data, status: response.status }
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { error?: string } }
        message?: string
      }
      return {
        status: err.response?.status,
        error: err.response?.data?.error || err.message,
      }
    }
  },
}
